/**
 * TransferSourceStep - Step 2: Seleção da origem da transferência
 * 
 * v2.5.0: Adiciona suporte a departamentos (conversations_department)
 * v2.3.0: Permite selecionar de quem os registros serão transferidos:
 * - Agente (para conversas e negócios.assigned_to)
 * - Vendedor (para negócios.seller_id e contatos.seller_id)
 * - Departamento (para conversations_department)
 * - "Sem atribuição" (__UNASSIGNED__) para registros não atribuídos
 */

import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useTenantMembers } from '@/hooks/useTenantMembers';
import { TransferType, TRANSFER_TYPE_LABELS, UNASSIGNED_SOURCE } from '@/hooks/useBulkTransfer';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { Search, Check, User, UserX, Building2 } from 'lucide-react';
import { cn } from '@/lib/utils';

// =============================================================================
// TIPOS
// =============================================================================

interface TransferSourceStepProps {
  transferType: TransferType;
  selectedId: string | null;
  onSelectId: (id: string) => void;
}

interface Agent {
  id: string;
  full_name: string;
  email: string;
  avatar_url: string | null;
}

interface Seller {
  id: string;
  name: string;
  email: string | null;
  avatar_url: string | null;
  user_id: string | null;
}

// v2.5.0: Interface para departamento
interface Department {
  id: string;
  name: string;
  description: string | null;
}

// =============================================================================
// COMPONENTE
// =============================================================================

export function TransferSourceStep({
  transferType,
  selectedId,
  onSelectId,
}: TransferSourceStepProps) {
  const { currentTenant } = useAuth();
  const [search, setSearch] = useState('');

  // Determinar tipo de transferência
  const isAgentTransfer = transferType === 'conversations' || transferType === 'deals_assigned';
  const isSellerTransfer = transferType === 'deals_seller' || transferType === 'contacts_seller';
  const isDepartmentTransfer = transferType === 'conversations_department'; // v2.5.0

  // =========================================================================
  // QUERY: AGENTES
  // =========================================================================

  // v2.0.0: Membros do tenant via hook centralizado (cache compartilhado, RPC server-side)
  const { data: tenantMembers = [], isLoading: isLoadingAgents } = useTenantMembers({
    onlyActive: true,
    enabled: isAgentTransfer && !!currentTenant?.id,
  });
  const agents: Agent[] | undefined = tenantMembers.map(m => ({
    id: m.user_id,
    full_name: m.full_name || '',
    email: m.email || '',
    avatar_url: m.avatar_url,
  }));

  // =========================================================================
  // QUERY: VENDEDORES
  // =========================================================================

  const { data: sellers, isLoading: isLoadingSellers } = useQuery({
    queryKey: ['transfer-sellers', currentTenant?.id],
    queryFn: async () => {
      if (!currentTenant?.id) return [];

      const { data, error } = await supabase
        .from('crm_sellers')
        .select(`
          id,
          name,
          email,
          user_id,
          profiles:user_id (avatar_url)
        `)
        .eq('tenant_id', currentTenant.id)
        .eq('is_active', true)
        .order('name');

      if (error) throw error;

      return (data || []).map((s) => ({
        id: s.id,
        name: s.name,
        email: s.email,
        user_id: s.user_id,
        avatar_url: (s.profiles as { avatar_url: string | null } | null)?.avatar_url || null,
      })) as Seller[];
    },
    enabled: isSellerTransfer && !!currentTenant?.id,
  });

  // =========================================================================
  // v2.5.0: QUERY: DEPARTAMENTOS
  // =========================================================================

  const { data: departments, isLoading: isLoadingDepartments } = useQuery({
    queryKey: ['transfer-departments-source', currentTenant?.id],
    queryFn: async () => {
      if (!currentTenant?.id) return [];

      const { data, error } = await supabase
        .from('departments')
        .select('id, name, description')
        .eq('tenant_id', currentTenant.id)
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      return (data || []) as Department[];
    },
    enabled: isDepartmentTransfer && !!currentTenant?.id,
  });

  // =========================================================================
  // FILTRAR ITENS
  // =========================================================================

  const filteredItems = useMemo(() => {
    const searchLower = search.toLowerCase();

    if (isAgentTransfer && agents) {
      return agents.filter(
        (a) =>
          a.full_name?.toLowerCase().includes(searchLower) ||
          a.email?.toLowerCase().includes(searchLower)
      );
    }

    if (isSellerTransfer && sellers) {
      return sellers.filter(
        (s) =>
          s.name?.toLowerCase().includes(searchLower) ||
          s.email?.toLowerCase().includes(searchLower)
      );
    }

    // v2.5.0: Filtrar departamentos
    if (isDepartmentTransfer && departments) {
      return departments.filter(
        (d) => d.name?.toLowerCase().includes(searchLower)
      );
    }

    return [];
  }, [isAgentTransfer, isSellerTransfer, isDepartmentTransfer, agents, sellers, departments, search]);

  const isLoading = isAgentTransfer 
    ? isLoadingAgents 
    : isSellerTransfer 
      ? isLoadingSellers 
      : isLoadingDepartments;

  // Label dinâmico baseado no tipo
  const entityLabel = isDepartmentTransfer ? 'departamento' : isAgentTransfer ? 'agente' : 'vendedor';

  // =========================================================================
  // RENDER
  // =========================================================================

  return (
    <div className="space-y-4">
      <div className="text-center mb-4">
        <h3 className="text-lg font-semibold">
          {isDepartmentTransfer ? 'De qual departamento transferir?' : 'De quem transferir?'}
        </h3>
        <p className="text-sm text-muted-foreground mt-1">
          Selecione {isDepartmentTransfer ? 'o departamento' : isAgentTransfer ? 'o agente' : 'o vendedor'} de origem para{' '}
          {TRANSFER_TYPE_LABELS[transferType].toLowerCase()}
        </p>
      </div>

      {/* Card especial para "Sem atribuição" / "Sem departamento" */}
      <Card
        className={cn(
          'cursor-pointer transition-all hover:border-amber-500/50 border-dashed',
          selectedId === UNASSIGNED_SOURCE && 'border-amber-500 bg-amber-500/5 ring-1 ring-amber-500'
        )}
        onClick={() => onSelectId(UNASSIGNED_SOURCE)}
      >
        <CardContent className="flex items-center gap-3 p-3">
          <div className="h-10 w-10 rounded-full bg-amber-500/10 flex items-center justify-center">
            {isDepartmentTransfer ? (
              <Building2 className="h-5 w-5 text-amber-600" />
            ) : (
              <UserX className="h-5 w-5 text-amber-600" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium">
              {isDepartmentTransfer ? 'Sem departamento' : 'Sem atribuição'}
            </p>
            <p className="text-sm text-muted-foreground">
              {isDepartmentTransfer
                ? 'Transferir conversas que não possuem departamento definido'
                : `Transferir registros que não possuem ${entityLabel} definido`}
            </p>
          </div>
          {selectedId === UNASSIGNED_SOURCE && (
            <div className="p-1 rounded-full bg-amber-500 text-white">
              <Check className="h-4 w-4" />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Divisor visual */}
      <div className="flex items-center gap-2">
        <div className="flex-1 h-px bg-border" />
        <span className="text-xs text-muted-foreground">ou selecione uma origem específica</span>
        <div className="flex-1 h-px bg-border" />
      </div>

      {/* Campo de busca */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder={`Buscar ${entityLabel}...`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Lista de itens */}
      <div className="max-h-[280px] overflow-y-auto space-y-2">
        {isLoading ? (
          // Skeletons de carregamento
          Array.from({ length: 4 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="flex items-center gap-3 p-3">
                <Skeleton className="h-10 w-10 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-48" />
                </div>
              </CardContent>
            </Card>
          ))
        ) : filteredItems.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            {isDepartmentTransfer ? (
              <Building2 className="h-12 w-12 mx-auto mb-2 opacity-50" />
            ) : (
              <User className="h-12 w-12 mx-auto mb-2 opacity-50" />
            )}
            <p>
              {search
                ? `Nenhum ${entityLabel} encontrado`
                : `Nenhum ${entityLabel} disponível`}
            </p>
          </div>
        ) : (
          filteredItems.map((item) => {
            // v2.5.0: Suporte a departamentos na lista
            const isDept = 'description' in item && !('email' in item && 'avatar_url' in item);
            const id = item.id;
            const name = 'full_name' in item ? item.full_name : item.name;
            const email = 'email' in item ? item.email : null;
            const avatarUrl = 'avatar_url' in item ? item.avatar_url : null;
            const isSelected = selectedId === id;

            return (
              <Card
                key={id}
                className={cn(
                  'cursor-pointer transition-all hover:border-primary/50',
                  isSelected && 'border-primary bg-primary/5 ring-1 ring-primary'
                )}
                onClick={() => onSelectId(id)}
              >
                <CardContent className="flex items-center gap-3 p-3">
                  {isDepartmentTransfer ? (
                    // Ícone de departamento em vez de avatar
                    <div className={cn(
                      "h-10 w-10 rounded-full flex items-center justify-center",
                      isSelected ? "bg-primary text-primary-foreground" : "bg-muted"
                    )}>
                      <Building2 className="h-5 w-5" />
                    </div>
                  ) : (
                    <Avatar>
                      <AvatarImage src={avatarUrl || undefined} />
                      <AvatarFallback>
                        {name?.substring(0, 2).toUpperCase() || '??'}
                      </AvatarFallback>
                    </Avatar>
                  )}

                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{name || 'Sem nome'}</p>
                    {email && (
                      <p className="text-sm text-muted-foreground truncate">
                        {email}
                      </p>
                    )}
                    {/* v2.5.0: Mostrar descrição do departamento se houver */}
                    {'description' in item && (item as Department).description && (
                      <p className="text-sm text-muted-foreground truncate">
                        {(item as Department).description}
                      </p>
                    )}
                  </div>

                  {isSelected && (
                    <div className="p-1 rounded-full bg-primary text-primary-foreground">
                      <Check className="h-4 w-4" />
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
}
